import Big from 'big.js'
import {format} from 'date-fns'

import DeliveryTruckSvg from '@/assets/food/icons/delivery-truck.svg?react'
import Price from '@/components/Price'
import PrimaryButton from '@/components/PrimaryButton/PrimaryButton'
import type {GrubhubOrder, Option} from '@/lib/food-grubhub/foodGrubhubModel'
import {FoodOrderRoute} from '@/routeRegistry'
import VisuallyHidden from '@/utils/a11y/VisuallyHidden'

import style from './FoodOrderItem.module.scss'
import {useRestaurantInfo} from './RestaurantInfo'

interface FoodOrderItemProps {
  readonly order: GrubhubOrder
  readonly isShowingDetails?: boolean
}

export default function FoodOrderItem({order, isShowingDetails = false}: FoodOrderItemProps) {
  const navigate = useNavigate()

  const restaurantId = order.charges.lines.lineItems[0]?.restaurantId
  const orderStatus = order.status.toUpperCase()
  const isFinalized = orderStatus === 'FINALIZED'
  const isDelivered = orderStatus === 'DELIVERED'

  const {renderRestaurantInfo, renderRestaurantInfoDetails, renderFoodImage} = useRestaurantInfo({
    restaurantId,
  })

  const formatDate = (date: Date, pattern: string) => {
    try {
      return format(date, pattern)
    } catch {
      return 'Invalid date'
    }
  }

  const getCustomizations = (options: Option[]): string => {
    const allCustomizations: string[] = []

    const processOption = (option: Option) => {
      if (option.name) {
        if (option.quantity > 1) {
          allCustomizations.push(`${option.quantity}x ${option.name}`)
        } else {
          allCustomizations.push(option.name)
        }
      }

      if (option.childOptions && option.childOptions.length > 0) {
        option.childOptions.forEach(childOption => {
          processOption(childOption)
        })
      }
    }

    options.forEach(option => {
      processOption(option)
    })

    return allCustomizations.join(', ')
  }

  const formatStatus = (status: string): string => {
    return status
      .toLowerCase()
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')
  }

  // Manual total calculation: dinerGrandTotal + Change to NFB - Discount
  const total = (() => {
    const subtotal = Big(order.charges.lines.dinerTotal)
    const discountValue = order.moreInfo.amountDiscount
      ? Big(-1).times(Big(order.moreInfo.amountDiscount))
      : Big(0)
    const deliveryFee = Big(order.charges.fees.delivery)
    const serviceFee = Big(order.charges.fees.service)
    const totalBeforeTax = subtotal.add(deliveryFee).add(serviceFee).add(discountValue)
    const tax = Big(order.charges.taxes.total)
    const driverTip = Big(order.moreInfo.driverTip)
    const roundUp = Big(order.moreInfo.amountRoundUp)
    return totalBeforeTax.add(tax).add(driverTip).add(roundUp)
  })()

  const renderDeliveredStatus = () => (
    <>
      <div className={style.Header}>
        <h3 className={style.DeliveryHeaderText}>
          Delivered on {formatDate(order.updatedAt, 'EEE, MMM d h:mm a')}
        </h3>
      </div>
      <div className={style.DeliveryStatus}>
        <div className={style.StatusBadge}>
          <span className={style.BadgeText}>
            <VisuallyHidden>Status: Delivered</VisuallyHidden>
            <span aria-hidden='true'>Delivered</span>
          </span>
        </div>
      </div>
    </>
  )

  const renderTrackingStatus = () => (
    <>
      <div className={style.DeliveryStatusActive}>
        <div className={style.DeliveryTime}>
          Estimated delivery: {order.orderingInfo.estimateRange.minimum} -{' '}
          {order.orderingInfo.estimateRange.maximum} minutes
        </div>
      </div>
      <div className={style.DeliveryStatus}>
        <div className={`${style.StatusBadge} ${style.Tracking}`}>
          <DeliveryTruckSvg
            aria-hidden='true'
            width={16}
            height={16}
            className={style.DeliveryTruckIcon}
          />
          <span className={style.BadgeText}>
            <VisuallyHidden>Status: Out for delivery</VisuallyHidden>
            <span aria-hidden='true'>Out for delivery</span>
          </span>
        </div>
      </div>
    </>
  )

  const renderOtherStatus = () => (
    <div className={style.DeliveryStatus}>
      <div className={style.StatusBadge}>
        <span className={style.BadgeText}>
          <VisuallyHidden>Status: {formatStatus(order.status)}</VisuallyHidden>
          <span aria-hidden='true'>{formatStatus(order.status)}</span>
        </span>
      </div>
    </div>
  )

  const getStatusContent = () => {
    if (isDelivered) {
      return renderDeliveredStatus()
    }
    if (isFinalized) {
      return renderTrackingStatus()
    }
    return renderOtherStatus()
  }

  return (
    <div className={`${style.Order} ${isShowingDetails ? style.DetailsView : ''}`}>
      <div className={style.OrderDetails}>
        <div className={style.Header}>
          <div>
            <h2 className={style.OrderId} aria-label={`Food order ${order.id}`}>
              Order #{order.id}
            </h2>
            <div className={style.Time}>
              {isShowingDetails
                ? `Placed on ${formatDate(order.createdAt, 'EEE, MMM d h:mm a')}`
                : `Placed on ${formatDate(order.createdAt, 'dd MMM yyyy')}`}
            </div>
            <div className={style.Time}>
              {isShowingDetails &&
                isDelivered &&
                `Delivered on ${formatDate(order.updatedAt, 'EEE, MMM d h:mm a')}`}
              {isShowingDetails && !isDelivered && !isFinalized && formatStatus(order.status)}
            </div>
          </div>
          <div className={style.TotalPrice}>
            <h3 aria-label='Order total'>Total Price</h3>
            <div>
              <Price price={total} />
            </div>
          </div>
          <div>
            <VisuallyHidden>
              <h3>Delivery Address</h3>
            </VisuallyHidden>
            <div
              className={style.ShippingInfo}
              aria-label={`Delivery to: ${order.shippingInfo.name}, ${order.shippingInfo.line1}, ${order.shippingInfo.city}, ${order.shippingInfo.state} ${order.shippingInfo.postalCode}`}
            >
              <div className={style.Name} aria-hidden>
                <span>Deliver to: </span>
                {order.shippingInfo.name}
              </div>
              <div className={style.Address} aria-hidden>
                <div className={style.AddressItem}>{order.shippingInfo.line1}</div>
                <div className={style.AddressItem}>
                  {order.shippingInfo.city}, {order.shippingInfo.state}{' '}
                  {order.shippingInfo.postalCode}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className={style.StoreInfo}>
          <VisuallyHidden>
            <h3>Restaurant</h3>
          </VisuallyHidden>
          {isShowingDetails ? renderRestaurantInfoDetails() : renderRestaurantInfo()}
        </div>
        <div className={style.FoodItems}>
          <VisuallyHidden>
            <h3>Order Items</h3>
          </VisuallyHidden>
          <div className={style.ItemsList}>
            {order.charges.lines.lineItems.map((item, index) => {
              const customizations = item.options ? getCustomizations(item.options) : ''
              return (
                <div key={item.menuItemId || index} className={style.ItemRow}>
                  <div className={style.ItemProduct}>
                    <div className={style.FoodItem}>
                      <div className={style.FoodItemImage}>
                        {renderFoodImage(item.menuItemId, item.name)}
                      </div>
                      <div className={style.FoodItemDetails}>
                        <div className={style.FoodItemName}>
                          <span>{item.name}</span>
                        </div>
                        {customizations && (
                          <div className={style.FoodItemNote}>
                            <VisuallyHidden>Customizations</VisuallyHidden>
                            {customizations}
                          </div>
                        )}
                        {isShowingDetails && (
                          <div className={style.ItemPriceMobile}>
                            <span>
                              <Price price={item.dinerTotal} />
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className={style.ItemQuantity}>
                    <VisuallyHidden>Quantity</VisuallyHidden>
                    {item.quantity}
                  </div>
                  {isShowingDetails && (
                    <div className={style.ItemPrice}>
                      <VisuallyHidden>Price:</VisuallyHidden>
                      <Price price={item.dinerTotal} />
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </div>
        {isShowingDetails && (
          <div className={style.OrderSummary}>
            <div className={style.OrderSummaryHeader}>
              <h3 className={style.SummaryLabel}>Order Summary</h3>
            </div>
            <div className={style.SummaryItems}>
              <div className={style.SummaryItem}>
                <span className={style.SummaryLabel}>
                  Subtotal ({order.charges.lines.lineItems.length} item
                  {order.charges.lines.lineItems.length !== 1 ? 's' : ''})
                </span>
                <span className={style.SummaryValue}>
                  <Price price={order.charges.dinerSubtotal} />
                </span>
              </div>
              <div className={style.SummaryItem}>
                <span className={style.SummaryLabel}>Delivery fee</span>
                <span className={style.SummaryValue}>
                  <Price price={order.charges.fees.delivery} />
                </span>
              </div>
              <div className={style.SummaryItem}>
                <span className={style.SummaryLabel}>Service fee</span>
                <span className={style.SummaryValue}>
                  <Price price={order.charges.fees.service} />
                </span>
              </div>
              <div className={style.SummaryItem}>
                <span className={style.SummaryLabel}>Sales tax</span>
                <span className={style.SummaryValue}>
                  <Price price={order.charges.taxes.total} />
                </span>
              </div>
              <div className={style.SummaryItem}>
                <span className={style.SummaryLabel}>Driver tips</span>
                <span className={style.SummaryValue}>
                  <Price price={order.moreInfo.driverTip} />
                </span>
              </div>
              <div className={style.SummaryItem}>
                <span className={style.SummaryLabel}>Donate change to NFB</span>
                <span className={style.SummaryValue}>
                  <Price price={order.moreInfo.amountRoundUp} />
                </span>
              </div>
              <div className={style.SummaryItem}>
                <span className={style.SummaryLabel}>Discount (Total)</span>
                <span className={`${style.SummaryValue} ${style.Discount}`}>
                  <Price price={Big(-1).times(Big(order.moreInfo.amountDiscount))} />
                </span>
              </div>
              <div className={`${style.SummaryItem} ${style.TotalRow}`}>
                <span className={style.SummaryLabel}>Total</span>
                <span className={`${style.SummaryValue} ${style.Total}`}>
                  <Price price={total} />
                </span>
              </div>
            </div>
          </div>
        )}
        {order.moreInfo.note && isShowingDetails && (
          <div className={style.NotesSection}>
            <VisuallyHidden>
              <h3>Order Notes</h3>
            </VisuallyHidden>
            <div className={style.NotesLabel} aria-hidden>
              Order Notes
            </div>
            <div className={style.NotesText}>"{order.moreInfo.note}"</div>
          </div>
        )}
      </div>
      {!isShowingDetails && (
        <div className={style.DeliveryInfo}>
          <VisuallyHidden>
            <h3>Order Status</h3>
          </VisuallyHidden>
          <div className={style.DeliveryStatusContainer}>
            <div className={style.DeliveryStatusLeft}>{getStatusContent()}</div>
            <div className={style.DeliveryStatusRight}>
              <PrimaryButton
                size='lg'
                className={isFinalized ? style.TrackOrderButton : style.ViewDetailButton}
                onPress={() => {
                  // if (isFinalized) {
                  //   void navigate({
                  //     to: '/food-tracking/$foodOrderId',
                  //     params: {foodOrderId: order.id},
                  //   })
                  // } else {
                  void navigate({
                    to: FoodOrderRoute.fullPath,
                    params: {foodOrderId: order.id},
                  })
                  // }
                }}
                aria-label={
                  isFinalized
                    ? `Track delivery for order ${order.id}`
                    : `View details for order ${order.id}`
                }
              >
                {/* {isFinalized ? 'Track your delivery order' : 'View details'} */}
                View details
              </PrimaryButton>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
